<script setup lang="ts">
import {
  AlertDialogRoot,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from 'radix-vue'

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    description?: string
    confirmLabel?: string
    cancelLabel?: string
    variant?: 'default' | 'destructive'
  }>(),
  {
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    variant: 'default',
  },
)

const emit = defineEmits<{
  'update:open': [value: boolean]
  confirm: []
  cancel: []
}>()

function onConfirm() {
  emit('confirm')
  emit('update:open', false)
}

function onCancel() {
  emit('cancel')
  emit('update:open', false)
}
</script>

<template>
  <AlertDialogRoot :open="open" @update:open="$emit('update:open', $event)">
    <AlertDialogPortal>
      <AlertDialogOverlay class="fixed inset-0 z-50 bg-black/75" />
      <AlertDialogContent
        class="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-surface-container border border-border rounded-lg p-6 shadow-2xl focus:outline-none"
      >
        <AlertDialogTitle class="font-headline font-black text-lg uppercase tracking-tight text-on-surface mb-2">
          {{ title }}
        </AlertDialogTitle>
        <AlertDialogDescription v-if="description" class="font-mono text-sm text-on-surface-variant mb-6">
          {{ description }}
        </AlertDialogDescription>

        <slot />

        <div class="mt-6 flex justify-end gap-2">
          <AlertDialogCancel
            class="inline-flex items-center justify-center font-mono text-[0.7rem] uppercase tracking-widest font-bold px-6 py-2.5 bg-surface-container-high text-on-surface hover:bg-surface-container-highest transition-all"
            @click="onCancel"
          >
            {{ cancelLabel }}
          </AlertDialogCancel>
          <AlertDialogAction
            :class="[
              'inline-flex items-center justify-center font-mono text-[0.7rem] uppercase tracking-widest font-bold px-6 py-2.5 transition-all',
              variant === 'destructive'
                ? 'bg-error-container text-error hover:brightness-110'
                : 'bg-primary-container text-white hover:brightness-110',
            ]"
            @click="onConfirm"
          >
            {{ confirmLabel }}
          </AlertDialogAction>
        </div>
      </AlertDialogContent>
    </AlertDialogPortal>
  </AlertDialogRoot>
</template>
